import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {Helmet} from "react-helmet";
import Context, { context_type } from '@Util/Context';
import { useTranslation } from 'react-i18next';
import * as Api from '@Util/Api';
import { apiBase } from '@Util/GlobalValues';
import moment from 'moment';
import { 
    write_console, 
    write_console_error,
    generate_unique_id,
    remove_unsupported_characters,
    monthOptions,
    weekUtils
} from '@Util/Utils';

import Page_Header from '@Elements/Page_Header';
import Content_Container from '@Elements/Content_Container';
import { H2 } from '@Elements/Fonts';
import Select_With_Autocomplete from '@Elements/FormElements/Select_With_Autocomplete';
import MultiSelect_With_Checkboxes from '@Elements/FormElements/MultiSelect_With_Checkboxes';
import Input_With_Label from '@Elements/FormElements/Input_With_Label';
import Textarea_With_Label from '@Elements/FormElements/Textarea_With_Label';
import Checkbox from '@Elements/FormElements/Checkbox';
import Datepicker_With_Label from '@Elements/FormElements/Datepicker_With_Label';
import Monthly_Report_Table from '@Elements/Monthly_Report_Table';
import Weekly_Report_Table from '@Elements/Weekly_Report_Table';

import { 
    PDF_Button, 
    Excel_Button 
} from '@Elements/Buttons';

import {
    Youtrack_Project,
    Select_With_Autocomplete_Options_Type,
    Customer,
    Monthly_Report,
    Weekly_Report
} from '@Util/Types';

export default function Reportspage(): JSX.Element {
    const [t] = useTranslation();
    const [visible_module, set_visible_module] = useState<"YTReport" | "MonthReport" | "WeekReport">("YTReport");

    return(
        <>
            <Helmet>
                <title>{`${t('Navigation.Report')} - InTime`}</title>
            </Helmet>
            <Page_Header headline={t('Navigation.Report')} />
            <div className="Tab">
                <div className="Tab_Navigation">
                    <button
                        type="button"
                        className={`Tab_Navigation_Button ${visible_module === "YTReport" ? "Tab_Navigation_Button--Active" : ""}`}
                        aria-controls=""
                        aria-expanded={visible_module === "YTReport" ? true : false}
                        onClick={() => set_visible_module("YTReport")}
                    >
                        {t('Label.YouTrackReport')}
                    </button>
                    <button
                        type="button"
                        className={`Tab_Navigation_Button ${visible_module === "MonthReport" ? "Tab_Navigation_Button--Active" : ""}`}
                        aria-controls=""
                        aria-expanded={visible_module === "MonthReport" ? true : false}
                        onClick={() => set_visible_module("MonthReport")}
                    >
                        {t('Label.MonthReport')}
                    </button>
                    <button
                        type="button"
                        className={`Tab_Navigation_Button ${visible_module === "WeekReport" ? "Tab_Navigation_Button--Active" : ""}`}
                        aria-controls=""
                        aria-expanded={visible_module === "WeekReport" ? true : false}
                        onClick={() => set_visible_module("WeekReport")}
                    >
                        {t('Label.WeekReport')}
                    </button>
                </div>
                <div className="Tab_Content">
                    {
                        visible_module === "YTReport" &&
                        <Reportspage_YTReport />
                    }
                    {
                        visible_module === "MonthReport" &&
                        <Reportspage_MonthlyReport />
                    }
                    {
                        visible_module === "WeekReport" &&
                        <Reportspage_WeeklyReport />
                    }
                </div>
            </div>
        </>
    )
}

function Reportspage_YTReport(): JSX.Element {
    const { set_status_update }: context_type = useContext(Context);
    const default_project_id: string = "35-80";
    const [t] = useTranslation();
    
    const first_day_of_month_at_noon: string = useMemo(() => {
        const now: Date = new Date();
        const firstDay: Date = new Date(now.getFullYear(), now.getMonth(), 1);
        firstDay.setHours(12, 0, 0, 0);
        return firstDay.toISOString();
    }, []);

    const last_day_of_month_at_noon: string = useMemo(() => {
        const now: Date = new Date();
        const lastDay: Date = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        lastDay.setHours(12, 0, 0, 0);
        return lastDay.toISOString();
    }, []);

    // YOUTRACK DATA
    const [youtrack_projects, set_youtrack_projects] = useState<Youtrack_Project[]>([]);
    const [youtrack_statusses, set_youtrack_statusses] = useState<string[]>([]);
    const [youtrack_fix_versions, set_youtrack_fix_versions] = useState<string[]>([]);
    const [youtrack_sub_systems, set_youtrack_sub_systems] = useState<string[]>([]);

    // REPORT DATA
    const [report_date_from, set_report_date_from] = useState<string>(first_day_of_month_at_noon);
    const [report_date_to, set_report_date_to] = useState<string>(last_day_of_month_at_noon);
    const [report_project, set_report_project] = useState<{ value: string, label: string }>({
        value: default_project_id,
        label: "Göteborg Energi Externwebb [publik]"
    });
    const [report_statusses, set_report_statusses] = useState<string[]>([]);
    const [report_fix_versions, set_report_fix_versions] = useState<string[]>([]);
    const [report_sub_systems, set_report_sub_systems] = useState<string[]>([]);
    const [report_multiple_files, set_report_multiple_files] = useState<boolean>(false);
    const [report_file_headline, set_report_file_headline] = useState<string>("");
    const [report_description, set_report_description] = useState<string>("");
    const [report_filename, set_report_filename] = useState<string>("");
    const [processing_pdf, set_processing_pdf] = useState<boolean>(false);
    const [processing_excel, set_processing_excel] = useState<boolean>(false);

    // FETCH YOUTRACK PROJECTS WHEN LOADING COMPONENT
    useEffect(() => {
        fetch_youtrack_projects();
        fetch_youtrack_statusses();
        fetch_youtrack_fix_versions();
        fetch_youtrack_sub_systems();
    }, []);

    // FETCH YOUTRACK DATA WHEN PROJECT HAS BEEN CHOSEN
    useEffect(() => {
        if (report_project) {
            fetch_youtrack_statusses(report_project.value);
            fetch_youtrack_fix_versions(report_project.value);
            fetch_youtrack_sub_systems(report_project.value);
        }
    }, [report_project]);

    const fetch_youtrack_projects = useCallback(async (): Promise<void> => {
        try {
            const projects = await Api.apiFetch(`/integration/youtrack/projects`);

            set_youtrack_projects(projects.projects);

            const default_project = projects?.projects?.find((defObject) => defObject.id === default_project_id);

            if (default_project) {
                set_report_project({
                    value: default_project.id,
                    label: default_project.name
                });
            }

            write_console(projects, "Fetching Youtrack Projects");
        } catch (err) {
            write_console_error("Error fetching Youtrack Projects", err);
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
        }
    }, [youtrack_projects, set_youtrack_projects]);

    const fetch_youtrack_statusses = useCallback(async (project_id?: string): Promise<void> => {
        try {
            const statusses = await Api.callApi(`/integration/youtrack/status`, {
                projectId: project_id || report_project.value
            });

            set_youtrack_statusses(statusses.status);

            write_console(statusses, "Fetching Youtrack Statusses");
        } catch (err) {
            write_console_error("Error fetching Youtrack Statusses", err);
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
        }
    }, [youtrack_statusses, set_youtrack_statusses]);

    const fetch_youtrack_fix_versions = useCallback(async (project_id?: string): Promise<void> => {
        try {
            const fix_versions = await Api.callApi(`/integration/youtrack/fix-versions`, {
                projectId: project_id || report_project.value
            });

            set_youtrack_fix_versions(fix_versions.fixVersions);

            write_console(fix_versions, "Fetching Youtrack Fix Versions");
        } catch (err) {
            write_console_error("Error fetching Youtrack Fix Versions", err);
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
        }
    }, [youtrack_fix_versions, set_youtrack_fix_versions]);

    const fetch_youtrack_sub_systems = useCallback(async (project_id?: string): Promise<void> => {
        try {
            const sub_systems = await Api.callApi(`/integration/youtrack/subsystems`, {
                projectId: project_id || report_project.value
            });

            set_youtrack_sub_systems(sub_systems.subSystems);

            write_console(sub_systems, "Fetching Youtrack Sub Systems");
        } catch (err) {
            write_console_error("Error fetching Youtrack Sub Systems", err);
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
        }
    }, [youtrack_sub_systems, set_youtrack_sub_systems]);

    function generate_file_name(extension: string): string {
        const current_time: string = moment().format("YYYY_MM_DD_HH_mm_ss");
        const file_extension: string = report_multiple_files ? ".zip" : `.${extension}`;

        let file_name = report_filename?.length >= 1 ? report_filename : `GE_YouTrack_Report_${current_time}${file_extension}`;

        if (report_sub_systems?.length >= 1) {
            let sub_system_name = report_sub_systems.join(" - ");
            file_name = file_name.replace("[sub-system]", sub_system_name);
        }

        // Replace [datum] with the current date
        file_name = file_name.replace("[datum]", current_time);

        return remove_unsupported_characters(file_name);
    }

    async function generate_and_download_report_PDF(): Promise<any> {
        try {
            set_processing_pdf(true);

            const form_data = {
                fixVersion: report_fix_versions,
                subSystems: report_sub_systems,
                stateFilters: report_statusses,
                headline: report_file_headline,
                description: report_description,
                createIndividualReports: report_multiple_files,
                fileName: report_filename,
                projectId: report_project.value,
                resolvedStart: report_date_from,
                resolvedEnd: report_date_to
                //resolvedStart: get_start_of_month(report_month_from?.value),
                //resolvedEnd: get_end_of_month(report_month_to?.value)
            };

            const api_call = await fetch(`/integration/youtrack/report-pdf`, {
                credentials: "same-origin",
                method: "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(form_data),
            });

            api_call.blob().then(blob => {
                let url = window.URL.createObjectURL(blob);
                let a = document.createElement('a');

                a.href = url;
                a.download = generate_file_name("pdf");
                document.body.appendChild(a);
                a.click();
                a.remove();
            });

            set_processing_pdf(false);
        } catch (err) {
            set_processing_pdf(false);
            write_console_error("Error generating and downloading YouTrack report as PDF", err);
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
        }
    }

    async function generate_and_download_report_excel(): Promise<any> {
        try {
            set_processing_excel(true);

            const form_data = {
                fixVersion: report_fix_versions,
                subSystems: report_sub_systems,
                stateFilters: report_statusses,
                createIndividualReports: report_multiple_files,
                fileName: report_filename,
                projectId: report_project.value,
                resolvedStart: report_date_from,
                resolvedEnd: report_date_to
                //resolvedStart: get_start_of_month(report_month_from?.value),
                //resolvedEnd: get_end_of_month(report_month_to?.value)
            };

            const api_call = await fetch(`/integration/youtrack/report-excel`, {
                credentials: "same-origin",
                method: "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(form_data),
            });

            api_call.blob().then(blob => {
                let url = window.URL.createObjectURL(blob);
                let a = document.createElement('a');

                a.href = url;
                a.download = generate_file_name("xlsx");
                document.body.appendChild(a);
                a.click();
                a.remove();
            });

            set_processing_excel(false);
        } catch (err) {
            set_processing_excel(false);
            write_console_error("Error generating and downloading YouTrack report as Excel", err);
            set_status_update({
                type: "Error",
                text: err.message,
                visible: true
            });
        }
    }

    return (
        <>
            <div className="Page_Content_Grid">
                <section className="Page_Content_Grid_Section">
                    <H2>{t('Text.Settings')}</H2>
                    <Content_Container>
                        <div className="YouTrack_Report_Grid">
                            <div>
                                <div className="YouTrack_Report_Grid_SplitInputs">
                                    <Datepicker_With_Label
                                        label={t('Label.MonthFrom')}
                                        selected={report_date_from}
                                        onChange={(date: Date) => set_report_date_from(date.toISOString())}
                                        id={generate_unique_id()}
                                        error={false}
                                        disableTime={true}
                                    />
                                    <Datepicker_With_Label
                                        label={t('Label.MonthTo')}
                                        selected={report_date_to}
                                        onChange={(date: Date) => set_report_date_to(date.toISOString())}
                                        id={generate_unique_id()}
                                        error={false}
                                        disableTime={true}
                                    />
                                </div>
                                <Select_With_Autocomplete
                                    label={t('Label.YouTrackProject')}
                                    value={report_project}
                                    onChange={(value: Select_With_Autocomplete_Options_Type) => {
                                        set_youtrack_statusses([]);
                                        set_youtrack_fix_versions([]);
                                        set_youtrack_sub_systems([]);
                                        set_report_statusses([]);
                                        set_report_fix_versions([]);
                                        set_report_sub_systems([]);
                                        set_report_project(value);
                                    }}
                                    options={youtrack_projects.map((project: Youtrack_Project) => {
                                        return {
                                            value: project.id,
                                            label: project.name
                                        }
                                    })}
                                />
                                {
                                    youtrack_statusses?.length > 0 &&
                                    <MultiSelect_With_Checkboxes
                                        label={`${t('Label.Status')} (${youtrack_statusses?.length})`}
                                        value={report_statusses}
                                        onChange={(e: any) => set_report_statusses(e.target.value)}
                                        options={youtrack_statusses}
                                    />
                                }
                                {
                                    youtrack_fix_versions?.length > 0 &&
                                    <MultiSelect_With_Checkboxes
                                        label={`${t('Label.FixVersions')} (${youtrack_fix_versions?.length})`}
                                        value={report_fix_versions}
                                        onChange={(e: any) => set_report_fix_versions(e.target.value)}
                                        options={youtrack_fix_versions}
                                    />
                                }
                                {
                                    youtrack_sub_systems?.length > 0 &&
                                    <MultiSelect_With_Checkboxes
                                        label={`${t('Label.SubSystems')} (${youtrack_sub_systems?.length})`}
                                        value={report_sub_systems}
                                        onChange={(e: any) => set_report_sub_systems(e.target.value)}
                                        options={youtrack_sub_systems}
                                    />
                                }
                                <Checkbox
                                    id={generate_unique_id()}
                                    label={t('Label.CreateMultipleReports')}
                                    checked={report_multiple_files}
                                    onChange={(e) => set_report_multiple_files(e.target.checked)}
                                    error={false}
                                />
                                <Input_With_Label
                                    label={t('Label.Filename')}
                                    value={report_filename}
                                    onChange={(e: any) => set_report_filename(e.target.value)}
                                    type="text"
                                    error={false}
                                    id={generate_unique_id()}
                                    info_text={t('Text.AvailableVariablesForReports')}
                                />
                                <Input_With_Label
                                    label={t('Label.Headline')}
                                    value={report_file_headline}
                                    onChange={(e: any) => set_report_file_headline(e.target.value)}
                                    type="text"
                                    error={false}
                                    id={generate_unique_id()}
                                    info_text={t('Text.AvailableVariablesForReports')}
                                />
                                <Textarea_With_Label
                                    label={t('Label.Description')}
                                    value={report_description}
                                    onChange={(e: any) => set_report_description(e.target.value)}
                                    error={false}
                                    id={generate_unique_id()}
                                    rows={5}
                                />
                            </div>
                        </div>
                    </Content_Container>
                </section>
                <section className="Page_Content_Grid_Section">
                    <H2>{t('Text.Download')}</H2>
                    <PDF_Button
                        onClick={generate_and_download_report_PDF}
                        loading={processing_pdf}
                    />
                    <Excel_Button
                        onClick={generate_and_download_report_excel}
                        loading={processing_excel}
                    />
                </section>
            </div>
        </>
    )
}

function Reportspage_MonthlyReport(): JSX.Element {
    const [t] = useTranslation();
    const { customers }: context_type = useContext(Context);
    const [report_month, set_report_month] = useState<moment.Moment>(moment().startOf('month'));
    const [report_customers, set_report_customers] = useState<any>([]);
    const [monthly_report_data, set_monthly_report_data] = useState<Monthly_Report>();
    const [excel_loading, set_excel_loading] = useState<boolean>(false);

    const customer_list = useMemo(() => {
        if (!customers || customers.length === 0) return [];

        const all_or_reset: string = report_customers?.length >= 1 ? "Reset" : "Alla";

        return [
            { id: all_or_reset, name: all_or_reset },
            ...customers.map(customer => ({
                id: customer.id.toString(),
                name: customer.name
            }))
        ];
    }, [customers, report_customers]);

    useEffect(() => {
        if (report_customers.length > 0) {
            generate_monthly_report();
        }
    }, [report_customers]);

    // GENERATES STRING LIST OF CUSTOMERS IDS FOR GENERATE MONTHLY REPORT FUNCTIONS
    async function generate_customer_list_with_ids(): Promise<string[]> {
        let objList: string[] = [];

        for (let i: number = 0; i < report_customers.length; i++) {
            let c: Customer = await customers.find((o) => o.name === report_customers[i]);
            objList.push(c.id.toString());
        }

        return objList;
    }

    async function generate_monthly_report(): Promise<void> {
        if (report_customers?.length >= 1) {
            let customerIds: string[] = await generate_customer_list_with_ids();
            let formData = { customerIds: customerIds, date: report_month.toJSON() };

            const o = await fetch(`${apiBase}/ScheduleEntry/monthlyReport`, {
                credentials: "same-origin",
                method: "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData),
            });

            const oJSON = await o.json();

            set_monthly_report_data(oJSON.result);
            write_console(oJSON, 'Fetching Monthly Report');

            if (oJSON?.status === "Error") {
                throw new Error("Generating monthly report: Received an error from the API");
            }
        }
    }

    function get_start_of_month(month_number: number, year?: number) {
        if (isNaN(month_number) || month_number < 1 || month_number > 12) {
            console.error('Invalid month number');
            return;
        }
        const reference_year = year || moment().year();
        const adjusted_month = month_number - 1;
        set_report_month(moment()
            .year(reference_year)
            .month(adjusted_month)
            .startOf('month'));
    }

    function handle_monthly_rapport_customers_change(e: any): void {
        if (e.includes("Alla")) {
            const allCustomers = customers.map(customer => customer.name);
            set_report_customers(allCustomers);
        } else if (e.includes("Reset")) {
            set_report_customers([]);
        } else {
            set_report_customers(e);
        }
    }

    async function generate_and_download_monthly_report(): Promise<any> {
        try {
            if (report_customers?.length >= 1) {
                set_excel_loading(true);
                const current_time = moment().format("YYYY-MMM-DD");
                let customerIds: string[] = await generate_customer_list_with_ids();
                let formData = { customerIds: customerIds, date: report_month.toJSON() };
                const o = await fetch(`${apiBase}/ScheduleEntry/monthlyReport/download`, {
                    credentials: "same-origin",
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(formData),
                });

                o.blob().then(blob => {
                    let url = window.URL.createObjectURL(blob);
                    let a = document.createElement('a');
                    a.href = url;
                    a.download = `InTime_Månadsrapport-${current_time}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                });
                set_excel_loading(false);
            }
        } catch (err) {
            set_excel_loading(false);
            write_console_error(err, err.message);
        }
    }

    const currentMonthValue = report_month
        ? monthOptions.find(option => option.value === (report_month.month() + 1).toString())
        : null;

    return (
        <>
            <H2>{t('Text.Settings')}</H2>
            <div className="Page_Content_Grid">
                <section className="Page_Content_Grid_Section">
                    <Content_Container>
                        <MultiSelect_With_Checkboxes
                            label={`${t('Label.Customer')}`}
                            className="Input--MaxWidth605"
                            value={report_customers}
                            onChange={(e) => handle_monthly_rapport_customers_change(e.target.value)}
                            options={customer_list.map((project: Youtrack_Project) => project.name)}
                        />
                        <Select_With_Autocomplete
                            label={t('Label.Month')}
                            className="Input--MaxWidth605"
                            value={currentMonthValue}
                            onChange={(selectedOption: Select_With_Autocomplete_Options_Type) => {
                                const monthNumber = parseInt(selectedOption.value, 10);
                                get_start_of_month(monthNumber);
                            }}
                            options={monthOptions}
                        />
                        <button className="Button Button_Blue Button--AlignRight" onClick={generate_monthly_report}>Generate Report</button>
                    </Content_Container>
                </section>
            </div>
            {
                monthly_report_data?.monthlyReportGrid?.length > 0 &&
                <>
                    <div className="Headline_Wrapper--WithAction">
                        <H2>{t('Label.MonthReport')}</H2>
                        <Excel_Button
                            onClick={generate_and_download_monthly_report}
                            loading={excel_loading}
                        />
                    </div>
                    <Monthly_Report_Table monthlyReportGrid={monthly_report_data?.monthlyReportGrid} />
                </>
            }
        </>
        
    )
}

function Reportspage_WeeklyReport(): JSX.Element {
    const [t] = useTranslation();
    const weekOptions = weekUtils.generateWeekOptions();
    const { customers }: context_type = useContext(Context);
    const [report_week, set_report_week] = useState<moment.Moment>(moment().startOf('isoWeek'));
    const [report_customers, set_report_customers] = useState<any>([]);
    const [weekly_report_data, set_weekly_report_data] = useState<Weekly_Report>();
    const [excel_loading, set_excel_loading] = useState<boolean>(false);

    const customer_list = useMemo(() => {
        if (!customers || customers.length === 0) return [];

        const all_or_reset: string = report_customers?.length >= 1 ? "Reset" : "Alla";

        return [
            { id: all_or_reset, name: all_or_reset },
            ...customers.map(customer => ({
                id: customer.id.toString(),
                name: customer.name
            }))
        ];
    }, [customers, report_customers]);

    useEffect(() => {
        if (report_customers.length > 0) {
            generate_weekly_report();
        }
    }, [report_customers]);

    // GENERATES STRING LIST OF CUSTOMERS IDS FOR GENERATE WEEKLY REPORT FUNCTIONS
    async function generate_customer_list_with_ids(): Promise<string[]> {
        let objList: string[] = [];

        for (let i: number = 0; i < report_customers.length; i++) {
            let c: Customer = await customers.find((o) => o.name === report_customers[i]);
            objList.push(c.id.toString());
        }

        return objList;
    }

    async function generate_weekly_report(): Promise<void> {
        if (report_customers?.length >= 1) {
            let customerIds: string[] = await generate_customer_list_with_ids();
            const weekRange = await weekUtils.getWeekRange(report_week.isoWeek(), report_week.isoWeekYear());
            let formData = {
                customerIds: customerIds,
                dateFrom: weekRange.start.toJSON(),
                dateTo: weekRange.end.toJSON(),
            };

            const o = await fetch(`${apiBase}/ScheduleEntry/weeklyReport`, {
                credentials: "same-origin",
                method: "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData),
            });

            const oJSON = await o.json();

            set_weekly_report_data(oJSON.result);
            write_console(oJSON, 'Fetching Weekly Report');

            if (oJSON?.status === "Error") {
                throw new Error("Generating weekly report: Received an error from the API");
            }
        }
    }

    function get_start_of_week_from_number(weekNumber: number, year?: number) {
        const referenceYear = year || moment().year();

        if (isNaN(weekNumber) || weekNumber < 1 || weekNumber > 53) {
            console.error('Invalid week number');
            return;
        }

        const startOfWeek = moment()
            .year(referenceYear)
            .isoWeek(weekNumber)
            .startOf('isoWeek');

        set_report_week(startOfWeek);
    }

    function handle_weekly_rapport_customers_change(e: any): void {
        if (e.includes("Alla")) {
            const allCustomers = customers.map(customer => customer.name);
            set_report_customers(allCustomers);
        } else if (e.includes("Reset")) {
            set_report_customers([]);
        } else {
            set_report_customers(e);
        }
    }

    async function generate_and_download_weekly_report(): Promise<any> {
        try {
            if (report_customers?.length >= 1) {
                set_excel_loading(true);
                const current_time = moment().format("YYYY-MMM-DD");
                let customerIds: string[] = await generate_customer_list_with_ids();
                let formData = { customerIds: customerIds, date: report_week.toJSON() };
                const o = await fetch(`${apiBase}/ScheduleEntry/monthlyReport/download`, {
                    credentials: "same-origin",
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(formData),
                });

                o.blob().then(blob => {
                    let url = window.URL.createObjectURL(blob);
                    let a = document.createElement('a');
                    a.href = url;
                    a.download = `InTime_Månadsrapport-${current_time}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                });
                set_excel_loading(false);
            }
        } catch (err) {
            set_excel_loading(false);
            write_console_error(err, err.message);
        }
    }

    const currentWeekValue = report_week
        ? weekOptions.find(option => option.value === report_week.isoWeek().toString())
        : null;

    return (
        <>
            <H2>{t('Text.Settings')}</H2>
            <div className="Page_Content_Grid">
                <section className="Page_Content_Grid_Section">
                    <Content_Container>
                        <MultiSelect_With_Checkboxes
                            label={`${t('Label.Customer')}`}
                            className="Input--MaxWidth605"
                            value={report_customers}
                            onChange={(e) => handle_weekly_rapport_customers_change(e.target.value)}
                            options={customer_list.map((project: Youtrack_Project) => project.name)}
                        />
                        <Select_With_Autocomplete
                            label={t('Label.Week')}
                            className="Input--MaxWidth605"
                            value={currentWeekValue}
                            onChange={(selectedOption: Select_With_Autocomplete_Options_Type) => {
                                const weekNumber = parseInt(selectedOption.value, 10);
                                get_start_of_week_from_number(weekNumber);
                            }}
                            options={weekOptions}
                        />
                        <button className="Button Button_Blue Button--AlignRight" onClick={generate_weekly_report}>Generate Report</button>
                    </Content_Container>
                </section>
            </div>
            {
                weekly_report_data?.weeklyReportGrid?.length > 0 &&
                <>
                    <div className="Headline_Wrapper--WithAction">
                        <H2>{t('Label.WeekReport')}</H2>
                        {/*
                            <Excel_Button
                                onClick={generate_and_download_weekly_report}
                                loading={excel_loading}
                            />
                        */}
                    </div>
                    <Weekly_Report_Table weeklyReportGrid={weekly_report_data?.weeklyReportGrid} />
                </>
            }
        </>

    )
}